
import React, { useEffect, useRef, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ChevronDown } from 'lucide-react';

export interface CodeSnippet {
  language: string;
  code: string;
}

interface CodeBlockProps {
  snippets: CodeSnippet[];
  defaultLanguage?: string;
  id?: string;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ snippets, defaultLanguage, id = 'code-block' }) => {
  // Use stored language preference if available and the snippet exists for that language
  const getInitialLanguage = (): string => {
    if (snippets.length <= 1) return snippets[0]?.language || 'typescript';
    const stored = localStorage.getItem('letsboostup_code_lang');
    if (stored && snippets.find(s => s.language === stored)) return stored;
    return defaultLanguage || snippets[0]?.language || 'typescript';
  };

  const [selectedLanguage, setSelectedLanguage] = useState<string>(getInitialLanguage());
  const [copied, setCopied] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!languageMenuRef.current?.contains(event.target as Node)) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isDropdownOpen]);

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang);
    setIsDropdownOpen(false);
    // Save preference for all code blocks on the page
    localStorage.setItem('letsboostup_code_lang', lang);
  };

  const currentSnippet = snippets.find(s => s.language === selectedLanguage) || snippets[0];
  const isPlainText = currentSnippet?.language === 'text';

  const copyToClipboard = async () => {
    if (currentSnippet) {
      await navigator.clipboard.writeText(currentSnippet.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const languageNames: Record<string, string> = {
    typescript: 'TypeScript',
    javascript: 'JavaScript',
    java: 'Java',
    python: 'Python',
    cpp: 'C++',
    csharp: 'C#',
    go: 'Go',
    rust: 'Rust',
    kotlin: 'Kotlin',
    swift: 'Swift',
    php: 'PHP',
    ruby: 'Ruby',
  };

  return (
    <div className={`article-code-block ${isPlainText ? 'article-text-visual' : ''} my-6 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-900 dark:bg-gray-950`}>
      {/* Header with Language Selector and Copy Button */}
      {!isPlainText && <div className="flex items-center justify-between px-4 py-3 bg-gray-800 dark:bg-gray-900 border-b border-gray-700 dark:border-gray-800">
        {/* Language Dropdown */}
        <div className="article-code-language" ref={languageMenuRef}>
          <button
            type="button"
            className="article-code-language__trigger"
            aria-label="Code language"
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            onClick={() => setIsDropdownOpen(open => !open)}
            onKeyDown={(event) => event.key === 'Escape' && setIsDropdownOpen(false)}
          >
            <span>{languageNames[selectedLanguage] || selectedLanguage}</span>
            <ChevronDown className={`w-4 h-4 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {isDropdownOpen && (
            <div className="article-code-language__menu" role="listbox" aria-label="Code language">
              {snippets.map((snippet) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedLanguage === snippet.language}
                  key={snippet.language}
                  className={`article-code-language__option ${selectedLanguage === snippet.language ? 'is-selected' : ''}`}
                  onClick={() => handleLanguageChange(snippet.language)}
                >
                  {languageNames[snippet.language] || snippet.language}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Copy Button */}
        <button
          onClick={copyToClipboard}
          className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 dark:bg-gray-800 hover:bg-gray-600 dark:hover:bg-gray-700 text-gray-200 dark:text-gray-300 rounded-md text-sm font-medium transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>}

      {/* Code Content */}
      {currentSnippet && (
        <div className="overflow-x-auto">
          <SyntaxHighlighter
            language={currentSnippet.language}
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '1rem',
              fontSize: '0.875rem',
              lineHeight: '1.6',
              background: '#1e1e1e',
            }}
            showLineNumbers={false}
            wrapLines={true}
          >
            {currentSnippet.code}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
};

export default CodeBlock;
